import { scopeGate, type AccountScope, type CopyStore, type ListCache } from '@heraion/shared';

// web-rnw twin of ./asyncListCache.ts (see that file's header) — same
// namespace + JSON shape, localStorage instead of AsyncStorage. Resolved
// instead of the bare file by Vite's `.web.ts` extension preference.

type Options = {
  version?: number;
};

const namespace = (cacheKey: string, version: number) =>
  `jl.asyncList.${cacheKey}.v${version}`;

export function asyncStorageListCache<T>(
  cacheKey: string,
  opts: Options = {},
): ListCache<T> {
  const storageKey = namespace(cacheKey, opts.version ?? 1);
  // #398: past an account change, no copy goes back (see scopeGate).
  const open = scopeGate(storageKey);
  return {
    read: async () => {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { items?: unknown } | null;
        if (!parsed || !Array.isArray(parsed.items)) return null;
        return parsed.items as T[];
      } catch {
        return null; // private mode / corrupt entry — treat as empty
      }
    },
    write: async (items, scope) => {
      if (!open(scope)) return false;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ cachedAt: new Date().toISOString(), items }),
        );
      } catch {
        // quota exceeded / private mode — non-fatal, network still drives.
      }
      return true;
    },
  };
}

export type ResourceCache<T> = CopyStore<T>;

/** The resource cache plus the age of what it holds. `cachedAtMs` null: a
 *  blob written without a date. */
export type AgedResourceCache<T> = ResourceCache<T> & {
  readEntry: () => Promise<{ value: T; cachedAtMs: number | null } | null>;
  /** Drop the blob (#336: a program editor draft that was saved or
   *  discarded). Best-effort like `write`. */
  clear: (scope: AccountScope) => Promise<void>;
};

export function asyncStorageResourceCache<T>(
  cacheKey: string,
  opts: Options = {},
): AgedResourceCache<T> {
  const storageKey = namespace(cacheKey, opts.version ?? 1);
  const open = scopeGate(storageKey);
  const readEntry = async () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { value?: unknown; cachedAt?: unknown } | null;
      if (!parsed || parsed.value === undefined) return null;
      const at = typeof parsed.cachedAt === 'string' ? Date.parse(parsed.cachedAt) : NaN;
      return { value: parsed.value as T, cachedAtMs: Number.isFinite(at) ? at : null };
    } catch {
      return null;
    }
  };
  return {
    readEntry,
    clear: async (scope) => {
      if (!open(scope)) return;
      try {
        await localStorage.removeItem(storageKey);
      } catch {
        // Best-effort, as write.
      }
    },
    read: async () => (await readEntry())?.value ?? null,
    write: async (value, scope) => {
      if (!open(scope)) return false;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ cachedAt: new Date().toISOString(), value }),
        );
      } catch {
        // quota exceeded / private mode — non-fatal.
      }
      return true;
    },
  };
}
